import React from "react";
import { Link, useParams } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { useTheme } from "@/context/ThemeContext";
import { brandedSrc, mediaUrl } from "@/lib/api";
import { localizedPath, pick } from "@/lib/helpers";

export function Logo({ className = "", textClass = "text-fg", onClick, forceDark = false }) {
  const { settings } = useSettings();
  const { theme } = useTheme();
  const { lang = "en" } = useParams();
  const useDarkLogo = forceDark || theme === "dark";
  const logoRef = useDarkLogo ? settings.logoDarkUrl || settings.logoUrl : settings.logoUrl;
  const name = pick({ en: settings.siteName_en, fr: settings.siteName_fr }, lang) || "Prime Technology and Consultancy Inc.";

  // The logo shows at <=240px wide on desktop, <=150px on mobile. Even with 2x
  // pixel ratio the largest pixel rendering is ~480 px. Request a WebP variant
  // sized for that — turns a 2.3 MB LinkedIn-source PNG into a ~10 KB WebP.
  // Append the same cache-busting tag brandedSrc uses so re-uploads invalidate.
  const logoSrc = logoRef
    ? (() => {
        const optimized = mediaUrl(logoRef, { w: 480, fmt: "webp", q: 85 });
        const tag = (String(logoRef).match(/[0-9a-fA-F-]{6,}/g) || []).pop();
        const v = tag ? tag.slice(-10) : "";
        return v ? `${optimized}&v=${v}` : optimized;
      })()
    : null;
  // Higher-res fallback for retina (2x) loaded only if browser supports srcSet.
  const logoSrcSet = logoRef
    ? `${mediaUrl(logoRef, { w: 240, fmt: "webp", q: 85 })} 240w, ${mediaUrl(logoRef, { w: 480, fmt: "webp", q: 85 })} 480w, ${mediaUrl(logoRef, { w: 720, fmt: "webp", q: 85 })} 720w`
    : undefined;

  return (
    <Link
      to={localizedPath(lang, "/")}
      onClick={onClick}
      aria-label={name}
      data-testid="site-logo"
      className={`inline-flex items-center gap-2.5 min-w-0 flex-shrink ${className}`}
    >
      {logoSrc ? (
        <img
          src={logoSrc}
          srcSet={logoSrcSet}
          sizes="(max-width: 640px) 150px, (max-width: 1024px) 200px, 240px"
          alt={name}
          width="240"
          decoding="async"
          fetchPriority="high"
          className="h-11 md:h-12 lg:h-14 w-auto max-w-[150px] sm:max-w-[200px] lg:max-w-[240px] object-contain"
        />
      ) : (
        <span className="inline-flex items-center gap-2.5 min-w-0">
          <span className="grid place-items-center w-10 h-10 border border-cyan/60 shrink-0">
            <span className="w-3.5 h-3.5 bg-cyan rotate-45" />
          </span>
          <span
            className={`font-display font-bold uppercase tracking-[0.08em] sm:tracking-[0.12em] text-sm sm:text-base md:text-lg lg:text-xl leading-tight ${textClass} truncate max-w-[200px] sm:max-w-[260px] md:max-w-[340px] lg:max-w-none`}
            title={name}
          >
            {name}
          </span>
        </span>
      )}
    </Link>
  );
}
